import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search } from "lucide-react";
import client from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { MemberBilling } from "@/components/billing/MemberBilling";

const inputCls = "rounded-xl bg-[#0d0d0d] border border-white/10 px-3 py-2 text-sm focus:border-[#ccff00]/50 focus:outline-none";
const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "-");

function IdentityStats() {
  const [s, setS] = useState(null);
  useEffect(() => { client.get("/admin/identity-stats").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const items = [["Total users", s.total], ["Email", s.email], ["Google", s.google], ["Wallet-enabled", s.wallet_enabled],
    ["Wallet-only", s.wallet_only], ["Linked wallets", s.linked_wallets], ["With nickname", s.with_nickname]];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6" data-testid="identity-stats">
      {items.map(([l, v]) => (
        <div key={l} className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
          <p className="text-[10px] font-mono-cl uppercase tracking-[0.14em] text-zinc-500">{l}</p>
          <p className="font-display text-2xl font-bold mt-1" data-testid={`identity-stat-${l.toLowerCase().replace(/\s+/g, "-")}`}>{v}</p>
        </div>
      ))}
    </div>
  );
}

function Section({ title, children }) {
  return (<div className="mt-6"><p className="text-[11px] font-mono-cl uppercase tracking-[0.16em] text-[#ccff00] mb-2">{title}</p>{children}</div>);
}

function MemberDetail({ uid, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const load = () => client.get(`/admin/members/${uid}`).then((r) => setD(r.data));
  useEffect(() => { if (uid) { setD(null); load(); } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);
  const act = async (path, body, msg) => {
    if (!window.confirm(`${msg.replace(/d$/, "")}: are you sure?`)) return;
    await client.post(`/admin/members/${uid}/${path}`, body); toast.success(msg); load(); onChanged(); };
  const reveal = async (w) => {
    const r = await client.post(`/admin/members/${uid}/wallets/${w.id}/reveal`);
    setD({ ...d, wallets: d.wallets.map((x) => (x.id === w.id ? { ...x, address: r.data.address } : x)) });
  };
  const i = d?.identity;
  return (
    <Sheet open={!!uid} onOpenChange={(o) => !o && onClose()}>
      <SheetContent data-testid="member-detail" className="bg-[#0b0b0b] border-white/10 w-full sm:max-w-xl overflow-y-auto">
        {!d ? <SheetHeader><SheetTitle className="text-zinc-500 text-sm font-normal">Loading…</SheetTitle></SheetHeader> : (
          <>
            <SheetHeader>
              <SheetTitle className="font-display text-2xl">{i.nickname ? `@${i.nickname}` : i.name}</SheetTitle>
              <p className="text-xs font-mono-cl text-zinc-500">{i.public_id} · {i.id}</p>
            </SheetHeader>
            <Section title="Identity">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-zinc-500">Name</span><span>{i.name}</span>
                <span className="text-zinc-500">Email</span><span className="break-all">{i.email || "- (wallet-only)"} {i.email && (i.email_verified ? "· verified" : "· unverified")}</span>
                <span className="text-zinc-500">Registered via</span><span>{i.registration_method}</span>
                <span className="text-zinc-500">Status</span><span data-testid="member-status">{i.status}</span>
                <span className="text-zinc-500">Joined</span><span>{fmt(i.created_at)}</span>
                <span className="text-zinc-500">Last login</span><span>{fmt(i.last_login_at)} {i.last_login_method ? `· ${i.last_login_method}` : ""}</span>
              </div>
            </Section>
            <Section title="Login methods"><p className="text-sm">{d.methods.map((m) => m.type + (m.usable ? "" : " (unverified)")).join(", ") || "None"}</p></Section>
            <Section title={`Linked wallets (${d.wallets.length})`}>
              {d.wallets.map((w) => (
                <div key={w.id} className="flex items-center justify-between text-sm py-1">
                  <span className="font-mono-cl break-all">{w.address} <span className="text-zinc-500">· {w.chain_name}{w.is_primary ? " · primary" : ""}</span></span>
                  {w.address.includes("…") && <button data-testid="member-wallet-reveal" onClick={() => reveal(w)} className="text-xs text-[#ccff00]">Reveal</button>}
                </div>
              ))}
              <p className="text-[11px] text-zinc-600 mt-1">Revealing a full address is recorded in the audit log.</p>
            </Section>
            <Section title="Whitelist"><p className="text-sm">{d.whitelist.status}{d.whitelist.plan ? ` · ${d.whitelist.plan}` : ""}</p></Section>
            <Section title="Alpha"><p className="text-sm">{d.alpha.categories.length ? `${d.alpha.categories.join(", ")} · ${d.alpha.access_type || ""} · expires ${fmt(d.alpha.expires_at)}` : "No active Alpha access"}</p></Section>
            <Section title="Analyst subscriptions">
              {d.analyst_subscriptions.length === 0 ? <p className="text-sm text-zinc-500">None</p> : d.analyst_subscriptions.map((s, k) => (
                <p key={k} className="text-sm">{s.analyst_name || s.analyst_id} · {s.active ? "active" : "ended"} · until {fmt(s.ends_at)}</p>))}
            </Section>
            <Section title="QRIS (Midtrans) orders">
              {d.billing.length === 0 ? <p className="text-sm text-zinc-500">No orders</p> : d.billing.map((o) => (
                <p key={o.order_id} className="text-sm">{fmt(o.created_at)} · {o.status} · Rp {Number(o.amount || 0).toLocaleString()}</p>))}
            </Section>
            <Section title="Security history">
              {d.security_events.slice(0, 15).map((e) => (
                <p key={e.id} className="text-xs text-zinc-400">{new Date(e.created_at).toLocaleString()} · {e.event}{e.meta?.method ? ` (${e.meta.method})` : ""}{e.meta?.new_ip ? " · new IP" : ""} · {e.ip}</p>))}
            </Section>
            <MemberBilling uid={uid} />
            <div className="flex gap-3 mt-8 flex-wrap">
              <button data-testid="member-revoke-sessions" onClick={() => act("revoke-sessions", {}, "Sessions revoked")} className="px-4 py-2 rounded-full border border-white/15 text-xs">Revoke sessions</button>
              <button data-testid="member-toggle-status" onClick={() => act("status", { status: i.status === "suspended" ? "active" : "suspended" }, "Status updated")}
                className="px-4 py-2 rounded-full border border-red-400/40 text-red-300 text-xs">{i.status === "suspended" ? "Reactivate" : "Suspend"}</button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

export function MemberIntel() {
  const [res, setRes] = useState({ rows: [], total: 0, page: 1, pages: 1 });
  const [f, setF] = useState({ q: "", method: "", wallet: "", verified: "", page: 1 });
  const [open, setOpen] = useState(null);
  const load = () => client.get("/admin/members", { params: f }).then((r) => setRes(r.data));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value, page: 1 });
  const rows = res.rows;

  return (
    <div data-testid="member-intel">
      <IdentityStats />
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]"><Search className="w-4 h-4 absolute left-3 top-2.5 text-zinc-500" />
          <input data-testid="members-search" className={inputCls + " w-full pl-9"} placeholder="Search name, email, @nickname, user ID" value={f.q} onChange={set("q")} /></div>
        <select data-testid="members-filter-method" className={inputCls} value={f.method} onChange={set("method")}><option value="">All methods</option><option value="email">Email</option><option value="google">Google</option><option value="wallet">Wallet</option></select>
        <select data-testid="members-filter-wallet" className={inputCls} value={f.wallet} onChange={set("wallet")}><option value="">Any wallets</option><option value="yes">Has wallet</option><option value="no">No wallet</option></select>
        <select data-testid="members-filter-verified" className={inputCls} value={f.verified} onChange={set("verified")}><option value="">Any email status</option><option value="yes">Verified</option><option value="no">Unverified</option></select>
      </div>
      <div className="overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full text-sm" data-testid="members-table">
          <thead className="text-[10px] font-mono-cl uppercase tracking-[0.14em] text-zinc-500 bg-white/[0.02]">
            <tr>{["User ID", "Nickname", "Name / email", "Method", "Wallets", "Email", "Whitelist", "Alpha", "Alpha expiry", "Analyst subs", "Last active"].map((h) => <th key={h} className="text-left px-3 py-2 whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid={`member-row-${r.id}`} onClick={() => setOpen(r.id)} className="border-t border-white/5 hover:bg-white/[0.03] cursor-pointer">
                <td className="px-3 py-2 font-mono-cl text-xs">{r.public_id}</td>
                <td className="px-3 py-2">{r.nickname ? `@${r.nickname}` : <span className="text-zinc-600">-</span>}</td>
                <td className="px-3 py-2"><p className="truncate max-w-[180px]">{r.name}</p><p className="text-xs text-zinc-500 truncate max-w-[180px]">{r.email || "wallet-only"}</p></td>
                <td className="px-3 py-2 capitalize">{r.registration_method}</td>
                <td className="px-3 py-2">{r.wallet_count}</td>
                <td className="px-3 py-2">{r.email_verified ? "Verified" : <span className="text-zinc-500">No</span>}</td>
                <td className="px-3 py-2">{r.whitelist_status}</td>
                <td className="px-3 py-2">{r.alpha_tier}</td>
                <td className="px-3 py-2 whitespace-nowrap">{fmt(r.alpha_expires_at)}</td>
                <td className="px-3 py-2">{r.analyst_subscriptions}</td>
                <td className="px-3 py-2 whitespace-nowrap">{fmt(r.last_active)}{r.status === "suspended" && <span className="ml-2 text-red-400 text-xs">suspended</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between mt-3 text-xs text-zinc-500">
        <span data-testid="members-total">{res.total} members</span>
        <div className="flex items-center gap-3">
          <button data-testid="members-prev" disabled={res.page <= 1} onClick={() => setF({ ...f, page: res.page - 1 })} className="disabled:opacity-30">Prev</button>
          <span>Page {res.page} / {res.pages}</span>
          <button data-testid="members-next" disabled={res.page >= res.pages} onClick={() => setF({ ...f, page: res.page + 1 })} className="disabled:opacity-30">Next</button>
        </div>
      </div>
      <MemberDetail uid={open} onClose={() => setOpen(null)} onChanged={load} />
    </div>
  );
}

export function AuditLogs() {
  const [logs, setLogs] = useState(null);
  const [reqs, setReqs] = useState(null);
  const load = () => { client.get("/admin/audit-logs").then((r) => setLogs(r.data)); client.get("/admin/recovery-requests").then((r) => setReqs(r.data)); };
  useEffect(load, []);
  const setStatus = async (id, status) => { await client.post(`/admin/recovery-requests/${id}`, { status }); load(); };
  return (
    <div className="grid gap-8 lg:grid-cols-2" data-testid="audit-logs">
      <div>
        <h3 className="font-display font-bold mb-3">Wallet recovery requests</h3>
        {reqs === null && <p className="text-sm text-zinc-500">Loading…</p>}
        {reqs?.length === 0 && <p className="text-sm text-zinc-500">None</p>}
        {reqs?.map((r) => (
          <div key={r.id} className="rounded-xl border border-white/10 p-3 mb-2 text-sm" data-testid={`recovery-${r.id}`}>
            <p className="font-mono-cl text-xs break-all">{r.address}</p>
            <p className="text-zinc-400 mt-1">{r.details}</p>
            <p className="text-xs text-zinc-500 mt-1">{r.contact_email} · {new Date(r.created_at).toLocaleString()}</p>
            <select data-testid="recovery-status" value={r.status} onChange={(e) => setStatus(r.id, e.target.value)} className={inputCls + " mt-2 text-xs"}>
              {["open", "in_review", "resolved", "rejected"].map((s) => <option key={s}>{s}</option>)}</select>
          </div>
        ))}
      </div>
      <div>
        <h3 className="font-display font-bold mb-3">Admin audit log</h3>
        {logs === null && <p className="text-sm text-zinc-500">Loading…</p>}
        {logs?.map((l) => (
          <p key={l.id} className="text-xs text-zinc-400 border-b border-white/5 py-1.5">{new Date(l.created_at).toLocaleString()} · <b className="text-zinc-200">{l.action}</b> · {l.actor_email}{l.target_user_id ? ` → ${l.target_user_id.slice(0, 8)}` : ""}</p>))}
        {logs?.length === 0 && <p className="text-sm text-zinc-500">No entries yet.</p>}
      </div>
    </div>
  );
}
